import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PrintIcon from '@mui/icons-material/Print';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { studentService, classService, userService } from '../../services/apiService';
import { formatDate } from '../../utils/dateFormat';
import { API_ENDPOINTS } from '../../config/api';

const steps = ['Parent/Guardian Details', 'Student Details', 'Review & Submit'];

const AddStudent = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingStudent, setLoadingStudent] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [createdStudentId, setCreatedStudentId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const drawerWidth = 280;

  // Form data state
  const [formData, setFormData] = useState({
    // Parent/Guardian Details
    parent_full_name: '',
    parent_email: '',
    parent_phone: '',
    parent_alternate_phone: '',
    parent_address: '',
    
    // Student Details
    full_name: '',
    father_name: '',
    mother_name: '',
    class_id: '',
    admission_date: '',
    roll_number: '',
    date_of_birth: '',
    gender: '',
    govt_id: '',
    govt_id_type: '',
    reg_no: '',
    promotion_status: 'current',
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [classes, setClasses] = useState([]);
  const [searchingParent, setSearchingParent] = useState(false);
  const lastSearchedPhoneRef = useRef('');

  // Fetch classes
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await classService.getAll();
        setClasses(response.data || response || []);
      } catch (err) {
        console.error('Error fetching classes:', err);
      }
    };
    fetchClasses();
  }, []);

  // Load student data when editing
  useEffect(() => {
    const fetchStudentData = async () => {
      if (!isEditMode || !id) return;
      
      try {
        setLoadingStudent(true);
        setError(null);
        const response = await studentService.getById(id);
        const student = response?.data || response;
        
        if (student) {
          // Populate form with student data
          setFormData({
            parent_full_name: student.parent_name || '',
            parent_email: student.parent_email || '',
            parent_phone: student.parent_phone || '',
            parent_alternate_phone: student.alternate_phone_number || '',
            parent_address: student.parent_address || '',
            full_name: student.full_name || '',
            father_name: student.father_name || '',
            mother_name: student.mother_name || '',
            class_id: student.class_id || '',
            admission_date: student.admission_date || '',
            roll_number: student.roll_number || '',
            date_of_birth: student.date_of_birth || '',
            gender: student.gender || '',
            govt_id: student.govt_id || '',
            govt_id_type: student.govt_id_type || '',
            reg_no: student.reg_no || '',
            promotion_status: student.promotion_status || 'current',
            is_active: student.is_active !== undefined ? student.is_active : true,
          });
        }
      } catch (err) {
        console.error('Error fetching student data:', err);
        setError(err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to load student data');
      } finally {
        setLoadingStudent(false);
      }
    };
    
    fetchStudentData();
  }, [isEditMode, id]);

  // Auto-search parent when phone number is entered
  useEffect(() => {
    const phone = formData.parent_phone?.trim();
    
    // Reset search cache if phone is cleared or invalid
    const phoneRegex = /^[9876]\d{9}$/;
    if (!phone || !phoneRegex.test(phone)) {
      if (lastSearchedPhoneRef.current) {
        lastSearchedPhoneRef.current = '';
      }
      return;
    }

    // Don't search if we already searched for this phone number
    if (lastSearchedPhoneRef.current === phone) {
      return;
    }

    const searchParentByPhone = async () => {
      // Don't search if all fields are already filled (user might be editing)
      if (formData.parent_full_name && formData.parent_email && formData.parent_address) {
        return;
      }

      setSearchingParent(true);
      lastSearchedPhoneRef.current = phone;

      try {
        const response = await userService.getAll({ phone });
        const users = response.data || response || [];
        const foundParent = users.find(u => u.role_name?.toLowerCase() === 'parent');

        if (foundParent) {
          // Auto-fill parent details only if fields are empty
          setFormData(prev => ({
            ...prev,
            parent_full_name: prev.parent_full_name || foundParent.full_name || '',
            parent_email: prev.parent_email || foundParent.email || '',
            parent_phone: foundParent.phone || prev.parent_phone,
            parent_address: prev.parent_address || foundParent.address || '',
          }));
        }
      } catch (error) {
        console.error('Error searching for parent:', error);
        // Silently fail - don't show error to user
      } finally {
        setSearchingParent(false);
      }
    };

    // Debounce the search
    const timeoutId = setTimeout(() => {
      searchParentByPhone();
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [formData.parent_phone, formData.parent_full_name, formData.parent_email, formData.parent_address]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };


  // Validate Step 1: Parent/Guardian Details
  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.parent_full_name?.trim()) {
      newErrors.parent_full_name = 'Parent full name is required';
    }
    // Email is optional, but if provided, it must be valid
    if (formData.parent_email?.trim() && !/\S+@\S+\.\S+/.test(formData.parent_email)) {
      newErrors.parent_email = 'Parent email is invalid';
    }
    if (!formData.parent_phone?.trim()) {
      newErrors.parent_phone = 'Parent phone number is required';
    } else {
      const phoneRegex = /^[9876]\d{9}$/;
      if (!phoneRegex.test(formData.parent_phone)) {
        newErrors.parent_phone = 'Phone number must start with 9,8,7 or 6 and be exactly 10 digits';
      }
    }
    if (!formData.parent_address?.trim()) {
      newErrors.parent_address = 'Parent address is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Validate Step 2: Student Details
  const validateStep2 = () => {
    const newErrors = {};

    if (!formData.full_name?.trim()) {
      newErrors.full_name = 'Student full name is required';
    }
    if (!formData.father_name?.trim()) {
      newErrors.father_name = 'Father name is required';
    }
    if (!formData.mother_name?.trim()) {
      newErrors.mother_name = 'Mother name is required';
    }
    if (!formData.class_id) {
      newErrors.class_id = 'Class is required';
    }
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    }
    if (!formData.admission_date) {
      newErrors.admission_date = 'Admission date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!validateStep1()) {
        return;
      }
    } else if (activeStep === 1) {
      if (!validateStep2()) {
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare student data
      const studentData = {
        full_name: formData.full_name,
        father_name: formData.father_name || null,
        mother_name: formData.mother_name || null,
        class_id: formData.class_id,
        roll_number: formData.roll_number,
        admission_date: formData.admission_date,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender || null,
        govt_id: formData.govt_id || null,
        govt_id_type: formData.govt_id_type || null,
        reg_no: formData.reg_no || null,
        promotion_status: formData.promotion_status || 'current',
        is_active: formData.is_active,
      };

      // Handle parent data
      studentData.parent_full_name = formData.parent_full_name;
      studentData.parent_email = formData.parent_email;
      studentData.parent_phone = formData.parent_phone;
      studentData.parent_alternate_phone = formData.parent_alternate_phone || null;
      studentData.parent_address = formData.parent_address;

      let response;
      if (isEditMode && id) {
        // Update existing student
        response = await studentService.update(id, studentData);
        setCreatedStudentId(id);
      } else {
        // Create new student
        response = await studentService.create(studentData);
        const studentId = response?.data?.id || response?.id || null;
        setCreatedStudentId(studentId);
      }
      
      setSuccess(true);
      setError(null);
    } catch (err) {
      const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Failed to create student';
      setError(errorMessage);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!createdStudentId) {
      alert('Student ID not available. Cannot generate PDF.');
      return;
    }

    try {
      // Generate PDF for this student
      const response = await fetch(`${API_ENDPOINTS.STUDENTS_GENERATE_PDF}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          student_id: createdStudentId,
          class_id: formData.class_id,
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to generate PDF' }));
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      // Get PDF blob
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `Student_${formData.full_name || 'Details'}_${new Date().toISOString().split('T')[0]}.pdf`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }
      
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(error.message || 'Failed to generate PDF. Please try again.');
    }
  };

  const handleGoBack = () => {
    navigate('/users/students');
  };

  // Render Step 1: Parent/Guardian Details
  const renderStep1 = () => (
    <Box sx={{ mt: { xs: 2, md: 3 } }}>
      <Stack spacing={{ xs: 2, md: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            label="Phone Number"
            value={formData.parent_phone}
            onChange={handleChange('parent_phone')}
            error={!!errors.parent_phone}
            helperText={errors.parent_phone || (searchingParent ? 'Searching...' : 'Must start with 9, 8, 7, or 6 and be exactly 10 digits. Auto-fills if parent exists.')}
            required
            InputProps={{
              endAdornment: searchingParent ? <CircularProgress size={20} /> : null,
            }}
          />

          <TextField
            fullWidth
            label="Alternate Phone Number"
            value={formData.parent_alternate_phone}
            onChange={handleChange('parent_alternate_phone')}
            helperText="Optional alternate contact number"
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            label="Parent/Guardian Full Name"
            value={formData.parent_full_name}
            onChange={handleChange('parent_full_name')}
            error={!!errors.parent_full_name}
            helperText={errors.parent_full_name}
            required
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            value={formData.parent_email}
            onChange={handleChange('parent_email')}
            error={!!errors.parent_email}
            helperText={errors.parent_email || 'Optional'}
          />
        </Box>

        <TextField
          fullWidth
          label="Address"
          multiline
          rows={3}
          value={formData.parent_address}
          onChange={handleChange('parent_address')}
          error={!!errors.parent_address}
          helperText={errors.parent_address}
          required
        />
      </Stack>
    </Box>
  );

  // Render Step 2: Student Details
  const renderStep2 = () => (
    <Box sx={{ mt: { xs: 2, md: 3 } }}>
      <Stack spacing={{ xs: 2, md: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            label="Student Full Name"
            value={formData.full_name}
            onChange={handleChange('full_name')}
            error={!!errors.full_name}
            helperText={errors.full_name}
            required
          />

          <FormControl fullWidth error={!!errors.class_id} required>
            <InputLabel>Class</InputLabel>
            <Select
              value={formData.class_id}
              onChange={handleChange('class_id')}
              label="Class"
            >
              <MenuItem value="">Select Class</MenuItem>
              {classes.map((cls) => (
                <MenuItem key={cls.id} value={cls.id}>
                  {cls.name} {cls.section ? `- ${cls.section}` : ''}
                </MenuItem>
              ))}
            </Select>
            {errors.class_id && <FormHelperText>{errors.class_id}</FormHelperText>}
          </FormControl>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            label="Father Name"
            value={formData.father_name}
            onChange={handleChange('father_name')}
            error={!!errors.father_name}
            helperText={errors.father_name}
            required
          />

          <TextField
            fullWidth
            label="Mother Name"
            value={formData.mother_name}
            onChange={handleChange('mother_name')}
            error={!!errors.mother_name}
            helperText={errors.mother_name}
            required
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            label="Date of Birth"
            type="date"
            value={formData.date_of_birth}
            onChange={handleChange('date_of_birth')}
            error={!!errors.date_of_birth}
            helperText={errors.date_of_birth}
            InputLabelProps={{ shrink: true }}
            required
          />

          <FormControl fullWidth>
            <InputLabel>Gender</InputLabel>
            <Select
              value={formData.gender}
              onChange={handleChange('gender')}
              label="Gender"
            >
              <MenuItem value="">Select Gender</MenuItem>
              <MenuItem value="M">Male</MenuItem>
              <MenuItem value="F">Female</MenuItem>
              <MenuItem value="O">Other</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            label="Admission Date"
            type="date"
            value={formData.admission_date}
            onChange={handleChange('admission_date')}
            error={!!errors.admission_date}
            helperText={errors.admission_date}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            fullWidth
            label="Roll Number"
            value={formData.roll_number}
            onChange={handleChange('roll_number')}
            helperText="Leave empty to auto-assign"
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
          <FormControl fullWidth>
            <InputLabel>Government ID Type</InputLabel>
            <Select
              value={formData.govt_id_type}
              onChange={handleChange('govt_id_type')}
              label="Government ID Type"
            >
              <MenuItem value="">Select ID Type</MenuItem>
              <MenuItem value="Aadhaar">Aadhaar</MenuItem>
              <MenuItem value="PAN">PAN</MenuItem>
              <MenuItem value="Voter ID">Voter ID</MenuItem>
              <MenuItem value="Driving License">Driving License</MenuItem>
              <MenuItem value="Passport">Passport</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Government ID"
            value={formData.govt_id}
            onChange={handleChange('govt_id')}
          />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            label="Registration Number"
            value={formData.reg_no}
            onChange={handleChange('reg_no')}
          />

          <FormControl fullWidth>
            <InputLabel>Promotion Status</InputLabel>
            <Select
              value={formData.promotion_status}
              onChange={handleChange('promotion_status')}
              label="Promotion Status"
            >
              <MenuItem value="current">Current</MenuItem>
              <MenuItem value="pass">Pass</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select
            value={formData.is_active ? 1 : 0}
            onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 1 }))}
            label="Status"
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Box>
  );

  // Render Step 3: Review & Submit
  const renderStep3 = () => (
    <Box sx={{ mt: { xs: 2, md: 3 } }}>
      <Stack spacing={{ xs: 2, md: 3 }}>
        {/* Parent Details Review */}
        <Card>
          <CardContent>
            <Typography 
              variant={isMobile ? 'subtitle2' : 'subtitle1'} 
              sx={{ 
                mb: { xs: 1.5, md: 2 }, 
                fontWeight: 700, 
                color: 'primary.main',
                fontSize: { xs: '0.875rem', md: '1rem' }
              }}
            >
              Parent/Guardian Details
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
              gap: { xs: 2, sm: 2, md: 3 } 
            }}>
              <Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                >
                  Full Name
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                >
                  {formData.parent_full_name || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                >
                  Email
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                >
                  {formData.parent_email || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                >
                  Phone
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                >
                  {formData.parent_phone || '—'}
                </Typography>
              </Box>
              {formData.parent_alternate_phone && (
                <Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Alternate Phone
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {formData.parent_alternate_phone}
                  </Typography>
                </Box>
              )}
              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1', md: '1 / -1' } }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                >
                  Address
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                >
                  {formData.parent_address || '—'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Student Details Review */}
        <Card>
          <CardContent>
            <Typography 
              variant={isMobile ? 'subtitle2' : 'subtitle1'} 
              sx={{ 
                mb: { xs: 1.5, md: 2 }, 
                fontWeight: 700, 
                color: 'primary.main',
                fontSize: { xs: '0.875rem', md: '1rem' }
              }}
            >
              Student Details
            </Typography>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, 
              gap: { xs: 2, sm: 2, md: 3 } 
            }}>
              <Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                >
                  Full Name
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                >
                  {formData.full_name || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Class</Typography>
                <Typography variant="body1">
                  {classes.find(c => c.id === formData.class_id)?.name || '—'}
                  {classes.find(c => c.id === formData.class_id)?.section ? ` - ${classes.find(c => c.id === formData.class_id)?.section}` : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                <Typography variant="body1">{formatDate(formData.date_of_birth) || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Gender</Typography>
                <Typography variant="body1">
                  {formData.gender === 'M' ? 'Male' : formData.gender === 'F' ? 'Female' : formData.gender === 'O' ? 'Other' : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Admission Date</Typography>
                <Typography variant="body1">{formatDate(formData.admission_date) || '—'}</Typography>
              </Box>
              {formData.father_name && (
                <Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Father Name
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {formData.father_name}
                  </Typography>
                </Box>
              )}
              {formData.mother_name && (
                <Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Mother Name
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {formData.mother_name}
                  </Typography>
                </Box>
              )}
              {formData.roll_number && (
                <Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Roll Number
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {formData.roll_number}
                  </Typography>
                </Box>
              )}
              {formData.govt_id_type && (
                <Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Government ID Type
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {formData.govt_id_type}
                  </Typography>
                </Box>
              )}
              {formData.govt_id && (
                <Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Government ID
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {formData.govt_id}
                  </Typography>
                </Box>
              )}
              {formData.reg_no && (
                <Box>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                  >
                    Registration Number
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                  >
                    {formData.reg_no}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                >
                  Promotion Status
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    textTransform: 'capitalize',
                    fontSize: { xs: '0.875rem', md: '1rem' }
                  }}
                >
                  {formData.promotion_status || '—'}
                </Typography>
              </Box>
              <Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', md: '0.75rem' } }}
                >
                  Status
                </Typography>
                <Typography 
                  variant="body1"
                  sx={{ fontSize: { xs: '0.875rem', md: '1rem' } }}
                >
                  {formData.is_active ? 'Active' : 'Inactive'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {error}
        </Alert>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        drawerWidth={drawerWidth}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 0,
          ml: { xs: 0, md: `${0}px` },
          backgroundColor: 'background.default',
        }}
      >
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <Box
          sx={{
            p: { xs: 2, sm: 2.5, md: 3 },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
          }}
        >
          <Paper sx={{ p: { xs: 2, sm: 3, md: 4 }, borderRadius: 2, boxShadow: 3 }}>
            {loadingStudent ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
              </Box>
            ) : success ? (
              // Success View
              <Box>
                <Alert 
                  severity="success" 
                  sx={{ 
                    mb: 3,
                    '& .MuiAlert-icon': {
                      fontSize: { xs: '1.5rem', md: '2rem' }
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {isEditMode ? 'Student Updated Successfully!' : 'Student Created Successfully!'}
                  </Typography>
                  <Typography variant="body1">
                    {isEditMode 
                      ? 'The student information has been successfully updated in the system.'
                      : 'The student has been successfully added to the system.'}
                  </Typography>
                </Alert>

                {/* Print and Go Back Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'center',
                  mt: 3
                }}>
                  <Button
                    variant="contained"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    fullWidth={isMobile}
                    size={isMobile ? 'medium' : 'large'}
                    sx={{ minWidth: { xs: '100%', sm: 150 } }}
                  >
                    Print Details
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={handleGoBack}
                    fullWidth={isMobile}
                    size={isMobile ? 'medium' : 'large'}
                    sx={{ minWidth: { xs: '100%', sm: 150 } }}
                  >
                    Go Back
                  </Button>
                </Box>

                {/* Print View - Student Details */}
                <Box sx={{ mt: 4, display: 'none', '@media print': { display: 'block' } }}>
                  {renderStep3()}
                </Box>
              </Box>
            ) : (
              // Form View
              <>
                {/* Header with Back Button and Title */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: { xs: 3, md: 4 }, 
                  position: 'relative',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 1, sm: 0 }
                }}>
                  <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/users/students')}
                    sx={{ 
                      position: { xs: 'relative', sm: 'absolute' },
                      left: { xs: 'auto', sm: 0 },
                      alignSelf: { xs: 'flex-start', sm: 'auto' },
                      mb: { xs: 1, sm: 0 }
                    }}
                    size={isMobile ? 'small' : 'medium'}
                  >
                    Back
                  </Button>
                  <Typography 
                    variant={isMobile ? 'h6' : 'h5'}
                    sx={{ 
                      fontWeight: 700, 
                      flex: 1, 
                      textAlign: { xs: 'left', sm: 'center' },
                      width: { xs: '100%', sm: 'auto' }
                    }}
                  >
                    {isEditMode ? 'Edit Student' : 'Add New Student'}
                  </Typography>
                </Box>

                {/* Stepper */}
                <Stepper 
                  activeStep={activeStep} 
                  sx={{ 
                    mb: { xs: 3, md: 4 },
                    '& .MuiStepLabel-root': {
                      '& .MuiStepLabel-label': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }
                      }
                    }
                  }}
                  orientation="horizontal"
                >
                  {steps.map((label) => (
                    <Step key={label}>
                      <StepLabel>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>

                {/* Step Content */}
                <Box sx={{ minHeight: { xs: 'auto', md: 400 } }}>
                  {activeStep === 0 && renderStep1()}
                  {activeStep === 1 && renderStep2()}
                  {activeStep === 2 && renderStep3()}
                </Box>

                {/* Navigation Buttons */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  mt: { xs: 3, md: 4 },
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    variant="outlined"
                    fullWidth={isMobile}
                    size={isMobile ? 'medium' : 'large'}
                  >
                    Back
                  </Button>
                  <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    {activeStep === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                        fullWidth={isMobile}
                        size={isMobile ? 'medium' : 'large'}
                      >
                        {loading ? 'Submitting...' : 'Submit'}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        fullWidth={isMobile}
                        size={isMobile ? 'medium' : 'large'}
                      >
                        Next
                      </Button>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default AddStudent;

